import * as React from 'react';

import type { FieldDef, StatusTone, BadgeVariantHint } from '@packages/core';
import { formatForDisplay } from '@packages/core';
import { getMaskedConfig } from '@packages/domain-legacy';
import { Typography } from '../../primitives/Typography';
import { palette } from '../../tokens/palette';

import { Icon } from '../../primitives/Icon';
import { Badge } from '../../primitives/Badge';
import { Tooltip } from '../../composites/Tooltip';
import { DescriptionList } from '../DescriptionList';
import type { DescriptionListItemLayout, DescriptionListItemSize } from '../DescriptionList';
import type { MaskedFieldConfig } from './types';
import { MaskedValue } from './MaskedValue';

export interface ViewFieldProps {
  def: FieldDef;
  value: unknown;
  icon?: string;
  layout?: DescriptionListItemLayout;
  /** Text size for inline/stacked layouts: 'sm' = 14px (default), 'md' = 16px. */
  size?: DescriptionListItemSize;
  action?: React.ReactNode;
  overrideLabel?: string;
  /** Override displayPrecision for this field when the primitive default differs from view context. */
  overridePrecision?: number;
  /** Override displayFormat for this specific field instance (e.g., render a 'text' primitive as 'badge'). */
  overrideDisplayFormat?: import('@packages/domain').DisplayFormat;
  /** Badge config override — template and variant for 'badge' displayFormat. */
  badge?: { template?: string; variant?: BadgeVariantHint };
  /** Pre-rendered value OR render function that bypasses formatForDisplay. Use for composite/multi-line values, badges, links. The function receives the field value and optionally the full data bag for computed fields. */
  renderValue?:
    | React.ReactNode
    | ((value: unknown, dataBag?: Record<string, unknown>) => React.ReactNode);
  /** Full data record passed to renderValue as 2nd arg. Enables computed fields that reference sibling values. */
  dataBag?: Record<string, unknown>;
  /** When present, renders a masked/revealable value with eye icon toggle + API call. */
  masked?: MaskedFieldConfig;
  /** Entity ID passed to masked unmaskFn. Required when masked is present. */
  entityId?: string;
  /** When true, value is truncated with ellipsis and a tooltip shows full text on overflow. */
  truncate?: boolean;
  /** When present, shows an info icon beside the label with tooltip text on hover. */
  tooltip?: string;
  /** Action ID emitted as an intent when clicked. Requires def.displayFormat = 'action-link'. */
  actionId?: string;
  /** Keys from dataBag to include in the intent payload. */
  actionPayloadKeys?: string[];
  /** When true, renders value without label (value-only mode). */
  hideLabel?: boolean;
  /** User-defined pass-through properties from domain.json displayOverrides. Available for custom renderers. */
  extras?: Record<string, unknown>;
}

function resolveDef(
  def: FieldDef,
  overridePrecision?: number,
  overrideDisplayFormat?: import('@packages/domain').DisplayFormat,
): FieldDef {
  if (overridePrecision == null && overrideDisplayFormat == null) return def;
  return {
    ...def,
    ...(overridePrecision != null && { displayPrecision: overridePrecision }),
    ...(overrideDisplayFormat != null && { displayFormat: overrideDisplayFormat }),
  };
}

const STATUS_TONE_COLORS: Record<StatusTone, string> = {
  success: 'var(--ui-color-success, #15803d)',
  error: 'var(--ui-color-error, #dc2626)',
  warning: 'var(--ui-color-warning, #d97706)',
  info: 'var(--ui-color-info, #2563eb)',
  neutral: 'var(--ui-text-muted, #6b7280)',
  primary: 'var(--ui-interactive, #7D55D3)',
};

function getMultilineEntries(
  value: unknown,
  keys?: readonly string[],
): string[] {
  if (value == null || typeof value !== 'object' || Array.isArray(value)) return [];
  const obj = value as Record<string, unknown>;
  const orderedKeys = keys ?? Object.keys(obj);
  return orderedKeys
    .map((k) => obj[k])
    .filter((v) => v != null && v !== '' && v !== false && typeof v !== 'object')
    .map(String);
}

function renderMultilineNode(value: unknown, keys?: readonly string[]): React.ReactNode {
  const lines = getMultilineEntries(value, keys);
  if (lines.length === 0) return undefined;
  return (
    <div>
      {lines.map((line, idx) => (
        <Typography key={idx} variant='body' color={palette.Black}>
          {line}
        </Typography>
      ))}
    </div>
  );
}

function renderMultilineListNode(value: unknown, keys?: readonly string[]): React.ReactNode {
  if (!Array.isArray(value) || value.length === 0) return undefined;
  const blocks = value
    .map((item, idx) => {
      const node = renderMultilineNode(item, keys);
      return node != null ? <div key={idx}>{node}</div> : null;
    })
    .filter(Boolean);
  if (blocks.length === 0) return undefined;
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
      {blocks}
    </div>
  );
}

function renderDisplayValue(displayText: string, def: FieldDef): React.ReactNode {
  if (def.displayFormat === 'html') {
    return <span dangerouslySetInnerHTML={{ __html: displayText }} />;
  }
  return displayText;
}

export function ViewField({
  def,
  value,
  icon,
  layout = 'stacked',
  size,
  action,
  overrideLabel,
  overridePrecision,
  overrideDisplayFormat,
  badge,
  renderValue,
  dataBag,
  masked,
  entityId,
  truncate,
  tooltip,
  actionId,
  actionPayloadKeys,
  hideLabel,
  extras: _extras,
}: ViewFieldProps) {
  const effectiveDef = resolveDef(def, overridePrecision, overrideDisplayFormat);
  const rawLabel = overrideLabel ?? def.label;
  const label = tooltip ? (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: '4px' }}>
      {rawLabel}
      <Tooltip.Root side='top' variant='dark' size='sm'>
        <Tooltip.Trigger asChild>
          <span style={{ display: 'inline-flex', cursor: 'pointer' }}>
            <Icon name='info' size='sm' filled />
          </span>
        </Tooltip.Trigger>
        <Tooltip.Content>{tooltip}</Tooltip.Content>
      </Tooltip.Root>
    </span>
  ) : (
    rawLabel
  );
  const resolvedMasked = masked ?? (entityId != null ? getMaskedConfig(def) : undefined);

  if (
    process.env.NODE_ENV === 'development' &&
    entityId == null &&
    masked == null &&
    getMaskedConfig(def) != null
  ) {
    console.warn(`[ViewField] "${def.label}" has masking registered but no entityId was provided.`);
  }

  let displayNode: React.ReactNode;
  if (resolvedMasked != null && entityId != null) {
    displayNode = <MaskedValue config={resolvedMasked} entityId={entityId} />;
  } else if (renderValue !== undefined) {
    const resolved = typeof renderValue === 'function' ? renderValue(value, dataBag) : renderValue;
    if (typeof resolved === 'number') {
      displayNode = formatForDisplay(resolved, effectiveDef);
    } else {
      displayNode = resolved;
    }
  } else if (
    effectiveDef.displayFormat === 'action-link' &&
    actionId != null &&
    value != null &&
    value !== ''
  ) {
    const displayText = formatForDisplay(value, effectiveDef);
    const payload: Record<string, unknown> = {};
    if (actionPayloadKeys != null && dataBag != null) {
      for (const k of actionPayloadKeys) {
        payload[k] = dataBag[k];
      }
    }
    const handleClick = () => {
      window.dispatchEvent(
        new CustomEvent('ui:action-intent', {
          detail: { type: 'OPEN_MODAL', actionId, payload },
        })
      );
    };
    displayNode = (
      <span
        role='button'
        tabIndex={0}
        onClick={handleClick}
        onKeyDown={(e: React.KeyboardEvent) => {
          if (e.key === 'Enter') handleClick();
        }}
        style={{
          color: 'var(--ui-color-primary, #6941C6)',
          cursor: 'pointer',
          textDecoration: 'underline',
        }}>
        {displayText}
      </span>
    );
  } else if (
    effectiveDef.displayFormat === 'status' &&
    effectiveDef.statusMap != null &&
    value != null &&
    value !== ''
  ) {
    const key = String(value);
    const entry = effectiveDef.statusMap[key] ?? effectiveDef.statusMap['_default'];
    if (entry != null) {
      const color = STATUS_TONE_COLORS[entry.tone];
      displayNode = entry.icon ? (
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: '4px' }}>
          <Icon name={entry.icon} size='sm' filled color={color} />
          {entry.label ?? key}
        </span>
      ) : (
        <span style={{ color }}>{entry.label ?? key}</span>
      );
    } else {
      displayNode = key;
    }
  } else if (effectiveDef.displayFormat === 'badge' && value != null && value !== '') {
    const template = badge?.template ?? effectiveDef.badgeTemplate;
    const variant = badge?.variant ?? (effectiveDef.badgeVariant as BadgeVariantHint) ?? 'default';
    const badgeText = template
      ? template.replace('{value}', String(value))
      : String(value);
    displayNode = (
      <Badge variant={variant} size='sm'>
        {badgeText}
      </Badge>
    );
  } else if (effectiveDef.displayFormat === 'multiline') {
    const mlKeys = (_extras as Record<string, unknown>)?.multilineKeys as string[] | undefined;
    displayNode = renderMultilineNode(value, mlKeys);
  } else if (effectiveDef.displayFormat === 'multiline-list') {
    const mlKeys = (_extras as Record<string, unknown>)?.multilineKeys as string[] | undefined;
    displayNode = renderMultilineListNode(value, mlKeys);
  } else {
    const displayText = formatForDisplay(value, effectiveDef);
    displayNode = renderDisplayValue(displayText, effectiveDef);
  }

  if (hideLabel) {
    const hasValue = displayNode != null && displayNode !== '';
    if (!hasValue && (value == null || value === '')) return null;
    const fontSize = size === 'md' ? '16px' : '14px';
    const fontWeight = size === 'md' ? 500 : 400;
    const color = size === 'md' ? 'var(--ui-text, #1a1a1a)' : 'var(--ui-text-muted, #6b7280)';
    return <div style={{ fontSize, fontWeight, color, lineHeight: 1.4 }}>{displayNode}</div>;
  }

  return (
    <DescriptionList.Item
      label={label}
      value={displayNode}
      icon={icon ? <Icon name={icon} size='sm' /> : undefined}
      layout={layout}
      size={size}
      action={action}
      placeholder={def.emptyText ?? '-'}
      truncate={truncate}
    />
  );
}
